import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Building2, MapPin, Users, Clock, Check } from "lucide-react";
import { IMAGES, CONTACT } from "@/data/classes";
import Seo from "@/components/Seo";

export default function GroupTraining() {
  return (
    <div>
      <Seo
        title="Group & Workplace Training"
        path="/group-training"
        description="On-site CPR, first aid, and OSHA-aligned workplace safety training for clinics, offices, warehouses, and businesses across the DFW area. Request a group quote."
      />
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="font-mono-label text-[#d90429] mb-4">Group & Workplace Training</div>
            <h1 className="font-display text-5xl md:text-6xl font-black text-[#0a0a0a] leading-none">
              We train
              <br />
              <span className="text-[#d90429]">your whole team.</span>
            </h1>
            <p className="mt-6 text-lg text-gray-700 max-w-2xl leading-relaxed">
              On-site training for clinics, medical practices, offices,
              warehouses, and any DFW workplace. Flexible scheduling, group
              pricing, and custom curriculum for your industry.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/contact?inquiry=group"
                data-testid="group-request-quote-cta"
                className="inline-flex items-center gap-2 rounded-md bg-[#d90429] px-8 py-4 text-base font-bold text-white hover:bg-black transition-colors"
              >
                Request a Group Quote <ArrowRight className="w-5 h-5" />
              </Link>
              <a
                href={CONTACT.phoneHref}
                className="inline-flex items-center gap-2 rounded-md border-2 border-[#0a0a0a] px-8 py-4 text-base font-bold text-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-white transition-colors"
              >
                Call {CONTACT.phone}
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <img
              src={IMAGES.workplace_training}
              alt="Workplace training"
              className="w-full h-96 object-cover rounded-lg border border-gray-200"
            />
          </div>
        </div>
      </section>

      <section className="bg-[#f9fafb] py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <h2 className="font-display text-4xl md:text-5xl font-black text-[#0a0a0a] max-w-3xl">
            How group training works
          </h2>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Users,
                title: "1. Tell us your team",
                body: "Share group size, industry, and which programs you need — BLS, ACLS, Heartsaver, or safety training.",
              },
              {
                icon: Clock,
                title: "2. We schedule around you",
                body: "Weekends, evenings, staggered shifts — we work with your operations, not against them.",
              },
              {
                icon: Building2,
                title: "3. We come to you",
                body: "On-site anywhere in DFW. We bring the manikins, AEDs, and instructor(s). You provide the room.",
              },
            ].map(({ icon: Icon, title, body }) => (
              <div key={title} className="bg-white border border-gray-200 rounded-lg p-8 hover-lift">
                <Icon className="w-8 h-8 text-[#d90429]" />
                <h3 className="mt-4 font-display text-2xl font-bold text-[#0a0a0a]">{title}</h3>
                <p className="mt-3 text-gray-600 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16">
          <div>
            <div className="font-mono-label text-[#d90429] mb-3">Best For</div>
            <h2 className="font-display text-3xl md:text-4xl font-black text-[#0a0a0a]">
              Organizations we serve
            </h2>
            <ul className="mt-8 space-y-3">
              {[
                "Medical & dental practices",
                "Skilled nursing & assisted living",
                "Schools, daycares, and camps",
                "Fitness studios & recreation centers",
                "Warehouses & manufacturing floors",
                "Corporate offices & hospitality teams",
                "Construction & field service teams",
              ].map((s) => (
                <li key={s} className="flex gap-3 text-gray-800">
                  <Check className="w-6 h-6 text-[#d90429] shrink-0" /> {s}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="font-mono-label text-[#d90429] mb-3">Included</div>
            <h2 className="font-display text-3xl md:text-4xl font-black text-[#0a0a0a]">
              What every group booking includes
            </h2>
            <ul className="mt-8 space-y-3">
              {[
                "Certified instructor(s) on-site",
                "All manikins, AED trainers, and materials",
                "Individual provider or completion cards",
                "Digital roster of certified attendees",
                "Optional custom modules for your industry",
                "Flexible scheduling incl. evenings and weekends",
              ].map((s) => (
                <li key={s} className="flex gap-3 text-gray-800">
                  <Check className="w-6 h-6 text-[#d90429] shrink-0" /> {s}
                </li>
              ))}
            </ul>

            <div className="mt-10 p-6 bg-[#0a0a0a] text-white rounded-lg flex items-start gap-4">
              <MapPin className="w-6 h-6 text-[#d90429] shrink-0 mt-1" />
              <div>
                <div className="font-display font-bold text-lg">Service Area</div>
                <p className="text-gray-300 mt-2 text-sm">
                  We travel across the DFW metroplex — Dallas, Fort Worth, Plano,
                  Frisco, Arlington, Irving, and surrounding cities.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#d90429]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 flex flex-wrap gap-8 justify-between items-center">
          <div>
            <h2 className="font-display text-3xl md:text-5xl font-black text-white leading-tight">
              Ready to train your team?
            </h2>
            <p className="mt-3 text-white/90 text-lg">
              Get a quote in one business day.
            </p>
          </div>
          <Link
            to="/contact?inquiry=group"
            data-testid="group-footer-cta"
            className="inline-flex items-center gap-2 rounded-md bg-white px-8 py-4 text-base font-bold text-[#d90429] hover:bg-black hover:text-white transition-colors"
          >
            Request a Quote <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
